/**
 * PulseBar — sticky 44px top bar. The founder's 2-second glance surface.
 *
 * Left → Right:
 *   status dot + label · decisions chip · legal chip · 24h spend · agents N/M ·
 *   replay button · "I am driving" button · GST clock
 *
 * Data: usePulseBarData hook (15s/30s polling)
 * "I am driving": POST /api/admin/ops/manual-override
 */
import { useState, useEffect, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import {
  Activity,
  Inbox,
  Scale,
  DollarSign,
  Cpu,
  Play,
  AlertTriangle,
  Clock,
} from "lucide-react";
import axios from "axios";
import { toast } from "sonner";
import { API } from "@/App";
import { getAdminToken } from "@/lib/authStorage";
import { usePulseBarData } from "./hooks/usePulseBarData";

// ── GST clock ────────────────────────────────────────────────────────────────

function GSTClock() {
  const [time, setTime] = useState("");
  useEffect(() => {
    function tick() {
      setTime(
        new Date().toLocaleTimeString("en-US", {
          timeZone: "Asia/Dubai",
          hour: "2-digit",
          minute: "2-digit",
          second: "2-digit",
          hour12: false,
        })
      );
    }
    tick();
    const t = setInterval(tick, 1000);
    return () => clearInterval(t);
  }, []);
  return (
    <span className="mc-pulse-clock" title="Gulf Standard Time (GST)">
      {time} GST
    </span>
  );
}

// ── StatusDot ────────────────────────────────────────────────────────────────

function StatusDot({ status }) {
  const label = status === "nominal"
    ? "Nominal"
    : status === "degraded"
    ? "Degraded"
    : status === "incident"
    ? "Incident"
    : "Unknown";

  const cls = `mc-status-dot mc-status-dot--${status || "unknown"}`;
  return (
    <span className="mc-pb-item mc-pb-status" title={`Platform: ${label}`}>
      <span className={cls} />
      <span className="mc-pb-label">{label}</span>
    </span>
  );
}

// ── Chip ─────────────────────────────────────────────────────────────────────

function Chip({ count, icon: Icon, onClick, variant = "neutral", title, label }) {
  const cls = `mc-chip mc-chip--${variant}`;
  return (
    <button className={`mc-pb-item ${cls}`} onClick={onClick} title={title}>
      {Icon && <Icon size={12} />}
      <span className="mc-chip-count">{count}</span>
      {label && <span className="mc-chip-label">{label}</span>}
    </button>
  );
}

// ── PulseBar ─────────────────────────────────────────────────────────────────

export default function PulseBar({ onLegalTickets }) {
  const navigate = useNavigate();
  const {
    health,
    healthError,
    pendingDecisions,
    maxStakes,
    activeAgents,
    totalAgents,
    cost24h,
    isManualOverrideActive,
  } = usePulseBarData();

  const [overriding, setOverriding] = useState(false);

  // Sync override state from polled data
  useEffect(() => {
    setOverriding(isManualOverrideActive);
  }, [isManualOverrideActive]);

  const toggleOverride = useCallback(async () => {
    const next = !overriding;
    const reason = next ? "Manual override activated from pulse bar" : "Disengaged from pulse bar";
    try {
      await axios.post(
        `${API}/admin/ops/manual-override`,
        { enabled: next, reason },
        { headers: { Authorization: `Bearer ${getAdminToken()}` } }
      );
      setOverriding(next);
      toast(next
        ? "⚠ I am driving — all agents paused"
        : "✓ Agents resumed"
      );
    } catch (err) {
      toast.error("Override failed — check console");
      console.error(err);
    }
  }, [overriding]);

  // decisions chip color
  const decisionsVariant =
    pendingDecisions === 0 ? "green"
    : pendingDecisions <= 3 ? "amber"
    : "critical";

  const healthStatus = health?.status || (healthError ? "incident" : "unknown");

  return (
    <header className="mc-pulse-bar">

      {/* ── Xpert brand lockup ── */}
      <div className="mc-pulse-brand" aria-label="Xpert Mission Control">
        <span className="mc-logo-mark" aria-hidden="true">
          <svg width="14" height="14" viewBox="0 0 24 24" fill="none">
            <path d="M5 12.5 L10 17.5 L19 7" stroke="#fff" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"/>
          </svg>
        </span>
        <span className="mc-pulse-brand-name">Xpert</span>
        <span className="mc-pulse-brand-sep" aria-hidden="true">·</span>
        <span className="mc-pulse-brand-sub">Mission Control</span>
      </div>

      <div className="mc-pb-left">
        <StatusDot status={healthStatus} />

        <Chip
          count={pendingDecisions}
          icon={Inbox}
          variant={decisionsVariant}
          title={`${pendingDecisions} pending decisions`}
          label="decisions"
          onClick={() => navigate("/mission-control/decisions")}
        />

        <span
          className="mc-pb-item mc-pb-cost"
          title="Agent spend in the last 24 hours"
          onClick={() => navigate("/mission-control/money")}
          role="button"
          style={{ cursor: "pointer" }}
        >
          <DollarSign size={12} />
          <span className="mc-pb-label mc-mono">
            ${cost24h.toFixed(2)}
          </span>
          <span className="mc-pb-sublabel">24h</span>
        </span>

        <span
          className="mc-pb-item mc-pb-agents"
          title={`${activeAgents} of ${totalAgents} platform agents active`}
          onClick={() => navigate("/mission-control/agents")}
          role="button"
          style={{ cursor: "pointer" }}
        >
          <Cpu size={12} />
          <span className="mc-pb-label mc-mono">
            {activeAgents}/{totalAgents}
          </span>
          <span className="mc-pb-sublabel">agents</span>
        </span>
      </div>

      <div className="mc-pb-right">
        <button
          className="mc-pb-item mc-pb-replay"
          title="Replay last 24 hours on constellation"
          onClick={() =>
            window.dispatchEvent(new CustomEvent("mc:replayConstellation"))
          }
        >
          <Play size={12} />
          <span className="mc-pb-label">Replay 24h</span>
        </button>

        <button
          className={`mc-pb-override${overriding ? " mc-pb-override--active" : ""}`}
          onClick={toggleOverride}
          title={
            overriding
              ? "Disengage — resume all agents"
              : "I am driving — pause all agents"
          }
        >
          {overriding ? (
            <>
              <AlertTriangle size={13} />
              DISENGAGE
            </>
          ) : (
            <>
              <Activity size={13} />
              I am driving
            </>
          )}
        </button>

        <GSTClock />
      </div>
    </header>
  );
}
